/** N8 survives the desktop entry reload, but belongs only to this tab/window. */
export interface SessionReturn {
  returnTo: string;
  userId: string;
  phase: 'prompt' | 'signing_in';
}

const key = 'auth.session-ended-return';

/** Only application routes may be restored; never navigate to a supplied origin. */
export const sessionReturnPath = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return;
  const url = new URL(value, window.location.origin);
  if (
    url.origin !== window.location.origin ||
    /^\/(?:login|logout|oauth|register)(?:\/|$)/u.test(url.pathname)
  )
    return;
  return `${url.pathname}${url.search}${url.hash}`;
};

export const readSessionReturn = (): SessionReturn | undefined => {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(key) ?? 'null') as SessionReturn | null;
    const returnTo = sessionReturnPath(value?.returnTo);
    if (
      returnTo &&
      typeof value?.userId === 'string' &&
      (value.phase === 'prompt' || value.phase === 'signing_in')
    ) {
      return { returnTo, userId: value.userId, phase: value.phase };
    }
  } catch {
    // Storage restrictions must not prevent signing in.
  }
  return undefined;
};

export const writeSessionReturn = (value: SessionReturn | undefined): void => {
  try {
    if (value) window.sessionStorage.setItem(key, JSON.stringify(value));
    else window.sessionStorage.removeItem(key);
  } catch {
    // The in-memory prompt still works when persistence is unavailable.
  }
};

export const isSessionEndedError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'error' in error &&
  error.error === 'login_required';
